<script setup lang="ts">
/**
 * API 金鑰的表單本體（批147）：名稱、權限範圍、到期——建立第一步與編輯共用。
 *
 * 草稿形狀與驗證在 `lib/apiKeys.ts`（純函式）；這裡只把輸入轉成新的草稿物件往上丟
 * （`update:modelValue`），不自己持有狀態。
 *
 * 到期是兩個選項＋一個日期欄：日期欄**一直都在**（「無期限」時只是停用），
 * 切換時下方的說明文字換掉、控制項本身不移位（切換類控制項不可讓位置跳動）。
 */
import { computed, nextTick, ref } from 'vue'

import ApiKeyScopePicker from '@/components/ApiKeyScopePicker.vue'
import type { ApiKeyScopeCatalog } from '@/api/ls'
import type { ApiKeyDraft, ExpiryMode } from '@/lib/apiKeys'
import { API_KEY_NAME_MAX, DEFAULT_EXPIRY_DAYS, daysFromToday, todayLocal } from '@/lib/apiKeys'

const props = defineProps<{ modelValue: ApiKeyDraft; catalog: ApiKeyScopeCatalog }>()
const emit = defineEmits<{ 'update:modelValue': [value: ApiKeyDraft] }>()

function patch(p: Partial<ApiKeyDraft>) {
  emit('update:modelValue', { ...props.modelValue, ...p })
}

const name = computed({
  get: () => props.modelValue.name,
  set: (v: string) => patch({ name: v }),
})
const scopes = computed({
  get: () => props.modelValue.scopes,
  set: (v: string[]) => patch({ scopes: v }),
})
const date = computed({
  get: () => props.modelValue.date,
  set: (v: string) => patch({ date: v }),
})

const dateInput = ref<HTMLInputElement | null>(null)
const today = computed(() => todayLocal())

async function setExpiry(mode: ExpiryMode) {
  if (mode === 'date' && !props.modelValue.date) {
    patch({ expiry: mode, date: daysFromToday(DEFAULT_EXPIRY_DAYS) })
  } else {
    patch({ expiry: mode })
  }
  if (mode === 'date') {
    await nextTick()
    dateInput.value?.focus()
  }
}
</script>

<template>
  <div class="akf">
    <label class="field">
      <span class="field-label">名稱</span>
      <input
        v-model="name"
        type="text"
        :maxlength="API_KEY_NAME_MAX"
        placeholder="例：每晚對帳腳本、Claude Code 維運"
        autocomplete="off"
      />
      <small class="subtle">只給人看（之後可改），建議寫用途與使用的程式。</small>
    </label>

    <!-- 到期放在權限範圍**之前**：勾選格底下還有一長串「網頁專屬」清單，放後面會被推到
         要捲很久才看得到（截圖驗收時抓到的）。 -->
    <div class="field akf-block">
      <span class="field-label">到期</span>
      <div class="akf-exp">
        <label class="akf-radio">
          <input
            type="radio"
            name="api-key-expiry"
            value="never"
            :checked="modelValue.expiry === 'never'"
            @change="setExpiry('never')"
          />
          無期限
        </label>
        <label class="akf-radio">
          <input
            type="radio"
            name="api-key-expiry"
            value="date"
            :checked="modelValue.expiry === 'date'"
            @change="setExpiry('date')"
          />
          指定日期
        </label>
        <input
          ref="dateInput"
          v-model="date"
          class="akf-date"
          type="date"
          :min="today"
          :disabled="modelValue.expiry !== 'date'"
          aria-label="到期日"
        />
      </div>
      <small class="subtle">
        {{
          modelValue.expiry === 'date'
            ? '到期日當天 23:59:59（本地時間）之後失效；到期後可以再編輯延長。'
            : '不會自動失效——不用了請撤銷。擁有者帳號停用、降級、被強制登出或刪除時會自動撤銷。'
        }}
      </small>
    </div>

    <div class="field akf-block">
      <span class="field-label">權限範圍</span>
      <ApiKeyScopePicker v-model="scopes" :catalog="catalog" />
    </div>
  </div>
</template>

<style scoped>
.akf-block {
  margin-top: var(--sp-4);
}
.akf-exp {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
}
.akf-radio {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-13);
  color: var(--fg-2);
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.akf-radio input {
  margin: 0;
}
.akf-date {
  width: 170px;
  max-width: 100%;
}
.akf-date:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
